import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../utils/supabase';
import {
  ShoppingBagIcon,
  UserIcon,
  HeartIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  HomeIcon,
  BuildingOfficeIcon,
  CubeIcon,
  Squares2X2Icon,
  ChevronDownIcon,
  RectangleStackIcon,
  CubeTransparentIcon,
} from '@heroicons/react/24/outline';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';

const Header = () => {
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isCartPreviewOpen, setIsCartPreviewOpen] = useState(false);
  const [categoriesTimeout, setCategoriesTimeout] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  
  // Arama state'leri
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  const navigate = useNavigate();
  const { getCartItemsCount } = useCart();
  const { user } = useAuth();

  // Icon mapping fonksiyonu
  const getIconForCategory = (categoryName) => {
    const name = categoryName.toLowerCase();
    
    if (name.includes('yatak') || name.includes('bedroom')) return CubeIcon;
    if (name.includes('oturma') || name.includes('koltuk') || name.includes('living')) return HomeIcon;
    if (name.includes('yemek') || name.includes('dining')) return Squares2X2Icon;
    if (name.includes('mutfak') || name.includes('kitchen')) return Squares2X2Icon;
    if (name.includes('banyo') || name.includes('bath')) return Squares2X2Icon;
    if (name.includes('bahçe') || name.includes('garden')) return RectangleStackIcon;
    if (name.includes('ofis') || name.includes('çalışma') || name.includes('office')) return BuildingOfficeIcon;
    if (name.includes('dekorasyon') || name.includes('aksesuar')) return RectangleStackIcon;
    
    // Varsayılan icon
    return HomeIcon;
  };

  // Kategorileri veritabanından çek
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        const { data, error } = await supabase
          .from('categories')
          .select('id, name, description')
          .eq('is_active', true)
          .order('name');

        if (error) {
          console.error('Header kategorileri yüklenirken hata:', error);
          return;
        }

        // Her kategori için ürün sayısını hesapla ve icon ekle
        const categoriesWithProductCount = await Promise.all(
          data.map(async (category) => {
            const { count: productCount } = await supabase
              .from('products')
              .select('*', { count: 'exact', head: true })
              .eq('category', category.name)
              .eq('is_active', true);

            return {
              ...category,
              icon: getIconForCategory(category.name),
              productCount: productCount || 0,
              subcategories: [`${productCount || 0} ürün mevcut`]
            };
          })
        );

        setCategories(categoriesWithProductCount);
      } catch (error) {
        console.error('Header kategori yükleme hatası:', error);
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Arama fonksiyonu
  const searchProducts = async (query) => {
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const { data, error } = await supabase
        .from('products')
        .select('id, name, category, image_url, images')
        .eq('is_active', true)
        .or(`name.ilike.%${query}%,description.ilike.%${query}%,category.ilike.%${query}%,tags.ilike.%${query}%`)
        .limit(8);

      if (error) {
        console.error('Arama hatası:', error);
        return;
      }

      setSearchResults(data || []);
    } catch (error) {
      console.error('Arama hatası:', error);
    } finally {
      setIsSearching(false);
    }
  };

  // Debounce için timeout ref
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Arama inputu değiştiğinde
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Önceki timeout'u temizle
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // Yeni timeout oluştur
    const timeoutId = setTimeout(() => {
      searchProducts(value);
    }, 300);
    
    setSearchTimeout(timeoutId);
  };

  // Enter tuşu basıldığında veya arama butonuna tıklandığında
  const handleSearch = (query = searchQuery) => {
    if (query && query.trim()) {
      navigate(`/products?search=${encodeURIComponent(query.trim())}`);
      setIsSearchOpen(false);
      setSearchQuery('');
      setSearchResults([]);
    }
  };

  // Popüler arama terimine tıklandığında
  const handlePopularSearchClick = (term) => {
    setSearchQuery(term);
    handleSearch(term);
  };

  // Arama sonucu ürününe tıklandığında
  const handleProductClick = (productId) => {
    navigate(`/products/${productId}`);
    setIsSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (categoriesTimeout) {
        clearTimeout(categoriesTimeout);
      }
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [categoriesTimeout, searchTimeout]);



  return (
    <>
      {/* Announcement Bar */}
      <div className="bg-gradient-to-r from-emerald-600 via-emerald-500 to-green-500 text-white py-3 sticky top-0 z-50 relative overflow-hidden">

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center">
                          <motion.p 
                className="text-sm font-medium text-center relative z-10 drop-shadow-lg"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                AVRUPA'NIN HER NOKTASINA GARANTİLİ KAPI ÖNÜ TESLİMAT
              </motion.p>
          </div>
        </div>
      </div>
      
      {/* Main Header */}
      <header className="bg-white/95 backdrop-blur-md border-b border-gray-100 sticky top-8 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            
            {/* Sol: Kategoriler Menüsü */}
            <div className="flex items-center">
              <div 
                className="relative"
                onMouseEnter={() => {
                  if (categoriesTimeout) {
                    clearTimeout(categoriesTimeout);
                    setCategoriesTimeout(null);
                  }
                  setIsCategoriesOpen(true);
                }}
                onMouseLeave={() => {
                  const timeout = setTimeout(() => {
                    setIsCategoriesOpen(false);
                  }, 150);
                  setCategoriesTimeout(timeout);
                }}
              >
                <motion.button
                  onClick={() => setIsCategoriesOpen(!isCategoriesOpen)}
                  className="flex items-center space-x-2 px-3 py-2 md:px-4 text-gray-700 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all duration-200"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <RectangleStackIcon className="h-5 w-5 md:h-6 md:w-6" />
                  <span className="font-medium text-sm md:text-base hidden sm:block">Kategoriler</span>
                  <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ${isCategoriesOpen ? 'rotate-180' : ''}`} />
                </motion.button>

                {/* Kategoriler Dropdown */}
                <AnimatePresence>
                  {isCategoriesOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full left-0 mt-2 w-72 sm:w-80 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50"
                    >
                      <div className="p-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Ürün Kategorileri</h3>
                        <div className="space-y-2">
                          {categoriesLoading ? (
                            // Loading skeleton
                            Array.from({ length: 4 }).map((_, index) => (
                              <div key={index} className="flex items-center space-x-3 p-3 rounded-lg">
                                <div className="h-6 w-6 bg-gray-200 rounded animate-pulse"></div>
                                <div className="flex-1 space-y-2">
                                  <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
                                  <div className="h-3 bg-gray-200 rounded w-20 animate-pulse"></div>
                                </div>
                              </div>
                            ))
                          ) : categories.length > 0 ? (
                            categories.map((category) => (
                              <Link 
                                key={category.id} 
                                to={`/products?category=${encodeURIComponent(category.name)}`}
                                className="group block"
                                onClick={() => setIsCategoriesOpen(false)}
                              >
                                <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-emerald-50 transition-colors duration-200 cursor-pointer">
                                  <category.icon className="h-6 w-6 text-emerald-600" />
                                  <div className="flex-1">
                                    <p className="font-medium text-gray-900 group-hover:text-emerald-600">{category.name}</p>
                                    <p className="text-sm text-gray-500">{category.productCount} ürün mevcut</p>
                                  </div>
                                  <ChevronDownIcon className="h-4 w-4 text-gray-400 group-hover:text-emerald-600" />
                                </div>
                              </Link>
                            ))
                          ) : (
                            // Boş durum
                            <div className="text-center py-4">
                              <p className="text-gray-500 text-sm">Henüz kategori bulunmuyor</p>
                            </div>
                          )}
                        </div>
                        <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
                          <Link 
                            to="/design" 
                            className="flex items-center justify-center space-x-2 w-full py-2 px-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg hover:from-blue-700 hover:to-cyan-600 transition-all duration-200 font-medium"
                          >
                            <CubeTransparentIcon className="h-4 w-4" />
                            <span>3D Tasarım Stüdyosu</span>
                          </Link>
                          <Link 
                            to="/products" 
                            className="block w-full text-center py-2 px-4 bg-gradient-to-r from-emerald-600 to-green-500 text-white rounded-lg hover:from-emerald-700 hover:to-green-600 transition-all duration-200 font-medium"
                          >
                            Tüm Ürünleri Görüntüle
                          </Link>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Orta: Logo */}
            <div className="flex-1 flex justify-center">
              <Link to="/" className="flex items-center">
                <motion.h1 
                  className="text-2xl md:text-3xl font-luxury font-bold text-black"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.2 }}
                >
                  FielHome
                </motion.h1>
              </Link>
            </div>

            {/* Sağ: İkonlar */}
            <div className="flex items-center space-x-2 md:space-x-3">
              
              {/* Arama */}
              <div className="relative">
                <motion.button
                  onClick={() => setIsSearchOpen(!isSearchOpen)}
                  className="p-2 md:p-3 text-gray-600 hover:text-emerald-600 hover:bg-emerald-50 rounded-full transition-all duration-200"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <MagnifyingGlassIcon className="h-5 w-5 md:h-6 md:w-6" />
                </motion.button>
              </div>

              {/* Favoriler */}
              <Link
                to="/favorites"
                className="relative p-2 md:p-3 text-gray-600 hover:text-emerald-600 hover:bg-emerald-50 rounded-full transition-all duration-200 group"
              >
                <HeartIcon className="h-5 w-5 md:h-6 md:w-6" />
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                  0
                </span>
              </Link>

              {/* Sepet */}
              <div className="relative">
                <Link
                  to="/cart"
                  onMouseEnter={() => setIsCartPreviewOpen(true)}
                  onMouseLeave={() => setIsCartPreviewOpen(false)}
                  className="relative p-2 md:p-3 text-gray-600 hover:text-emerald-600 hover:bg-emerald-50 rounded-full transition-all duration-200 group"
                >
                  <ShoppingBagIcon className="h-5 w-5 md:h-6 md:w-6" />
                  {getCartItemsCount() > 0 && (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute -top-1 -right-1 bg-gradient-to-r from-emerald-600 to-green-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-semibold"
                    >
                      {getCartItemsCount()}
                    </motion.span>
                  )}
                </Link>

                {/* Sepet Preview */}
                <AnimatePresence>
                  {isCartPreviewOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50"
                      onMouseEnter={() => setIsCartPreviewOpen(true)}
                      onMouseLeave={() => setIsCartPreviewOpen(false)}
                    >
                      <div className="p-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Sepetim</h3>
                        {getCartItemsCount() === 0 ? (
                          <div className="text-center py-8">
                            <ShoppingBagIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                            <p className="text-gray-500">Sepetiniz boş</p>
                            <Link 
                              to="/products" 
                              className="inline-block mt-3 px-4 py-2 bg-gradient-to-r from-emerald-600 to-green-500 text-white rounded-lg hover:from-emerald-700 hover:to-green-600 transition-all duration-200 text-sm font-medium"
                            >
                              Alışverişe Başla
                            </Link>
                          </div>
                        ) : (
                          <div>
                            <p className="text-sm text-gray-600 mb-4">{getCartItemsCount()} ürün sepetinizde</p>
                            <Link 
                              to="/cart" 
                              className="block w-full text-center py-2 px-4 bg-gradient-to-r from-emerald-600 to-green-500 text-white rounded-lg hover:from-emerald-700 hover:to-green-600 transition-all duration-200 font-medium"
                            >
                              Sepeti Görüntüle
                            </Link>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Kullanıcı Hesabı */}
              {user ? (
                <Link
                  to="/profile"
                  className="relative p-2 md:p-3 text-gray-600 hover:text-emerald-600 hover:bg-emerald-50 rounded-full transition-all duration-200"
                  title="Profilim"
                >
                  <UserIcon className="h-5 w-5 md:h-6 md:w-6" />
                  {user && !user.email_confirmed_at && (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center"
                      title="E-posta doğrulaması gerekli"
                    >
                      <ExclamationTriangleIcon className="h-3 w-3" />
                    </motion.span>
                  )}
                </Link>
              ) : (
                <Link
                  to="/auth"
                  className="px-3 py-2 md:px-4 text-xs md:text-sm font-medium text-white bg-gradient-to-r from-emerald-600 to-green-500 hover:from-emerald-700 hover:to-green-600 rounded-lg transition-all duration-200"
                >
                  <span className="hidden sm:block">Giriş Yap</span>
                  <span className="sm:hidden">Giriş</span>
                </Link>
              )}
          </div>
        </div>
      </div>

        {/* Gelişmiş Arama */}
        <AnimatePresence>
          {isSearchOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-gradient-to-r from-emerald-50 to-green-50 border-t border-emerald-100 overflow-hidden"
            >
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <div className="max-w-2xl mx-auto">
                  <div className="relative">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={handleSearchChange}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      placeholder="Mobilya, dekorasyon ve daha fazlası..."
                      className="w-full pl-12 pr-4 py-4 border-2 border-emerald-200 rounded-2xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-lg font-medium shadow-lg"
                      autoFocus
                    />
                    <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-6 w-6 text-emerald-400" />
                    
                    {/* Arama butonı */}
                    {searchQuery && (
                      <button
                        onClick={() => handleSearch()}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg transition-colors duration-200 font-medium"
                      >
                        Ara
                      </button>
                    )}
                  </div>
                  
                  {/* Arama Sonuçları */}
                  {searchQuery && searchQuery.length >= 2 && (
                    <div className="mt-4 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                      {isSearching ? (
                        <div className="p-4 text-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-500 mx-auto"></div>
                          <p className="text-gray-500 mt-2">Aranıyor...</p>
                        </div>
                      ) : searchResults.length > 0 ? (
                        <div className="max-h-80 overflow-y-auto">
                          {searchResults.map((product) => {
                            const productImage = product.images && product.images.length > 0 
                              ? (product.images[0].url || product.images[0]) 
                              : product.image_url;
                            
                            return (
                              <motion.div
                                key={product.id}
                                onClick={() => handleProductClick(product.id)}
                                className="flex items-center space-x-3 p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                                whileHover={{ backgroundColor: '#f9fafb' }}
                              >
                                <div className="w-12 h-12 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                                  {productImage ? (
                                    <img 
                                      src={productImage} 
                                      alt={product.name}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                                      <CubeIcon className="h-6 w-6 text-gray-400" />
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-gray-900 truncate">{product.name}</p>
                                  <p className="text-sm text-gray-500">{product.category}</p>
                                </div>
                              </motion.div>
                            );
                          })}
                          <div className="p-3 bg-gray-50 border-t border-gray-200 text-center">
                            <button
                              onClick={() => handleSearch()}
                              className="text-emerald-600 hover:text-emerald-700 font-medium text-sm"
                            >
                              Tüm sonuçları gör ({searchResults.length}+ ürün)
                            </button>
                          </div>
                        </div>
                      ) : searchQuery.length >= 2 && !isSearching ? (
                        <div className="p-4 text-center">
                          <p className="text-gray-500">"{searchQuery}" için sonuç bulunamadı</p>
                          <button
                            onClick={() => handleSearch()}
                            className="text-emerald-600 hover:text-emerald-700 font-medium text-sm mt-2"
                          >
                            Yine de ara
                          </button>
                        </div>
                      ) : null}
                    </div>
                  )}
                  
                  {/* Popüler Aramalar - Sadece arama sonucu yoksa göster */}
                  {!searchQuery && (
                    <div className="mt-6">
                      <p className="text-sm font-medium text-gray-600 mb-3">Popüler Aramalar:</p>
                      <div className="flex flex-wrap gap-2">
                        {['Koltuk Takımı', 'Yatak Odası', 'Yemek Masası', 'Gardırop', 'TV Ünitesi', 'Berjer'].map((term) => (
                          <button
                            key={term}
                            onClick={() => handlePopularSearchClick(term)}
                            className="px-4 py-2 bg-white text-emerald-600 rounded-full border border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300 transition-all duration-200 text-sm font-medium"
                          >
                            {term}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>


      </header>
    </>
  );
};

export default Header; 